import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Download, FileSpreadsheet, FileText, Filter, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { API, useAdminAuth } from '../context/AdminAuthContext';
import { DutyGate } from '../components/OffDutyScreen';

/**
 * Feb 2026 — AdminAttendance
 *
 * Super-admin only page that lists every admin's attendance for a
 * selectable month, computes overtime against an 8-hour base, and
 * lets the super admin export CSV / PDF with a chosen subset of
 * columns.
 *
 * Data comes from `/api/admin/attendance/log?month=YYYY-MM` — the
 * backend does all the heavy lifting (worked-hours + overtime math
 * from `first_login_at` + `last_activity_at` with a `duty_end`
 * fallback). This page is purely a rendering / composition layer.
 */

const HEADCARD = 'rounded-2xl border border-slate-200 bg-white p-4';

const nowMuscatMonthISO = () => {
  // Muscat is UTC+4 with no DST. Shift the current time by +4h and
  // read the YYYY-MM prefix so the picker defaults to whatever
  // Muscat's calendar says right now.
  const d = new Date();
  const shifted = new Date(d.getTime() + 4 * 60 * 60 * 1000);
  return shifted.toISOString().slice(0, 7);
};

const AdminAttendance = () => {
  const { authHeaders, isSuperAdmin } = useAdminAuth();
  const [month, setMonth] = useState(() => nowMuscatMonthISO());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState('');   // 'csv' | 'pdf' | ''
  const [expanded, setExpanded] = useState(() => new Set());
  const [selectedCols, setSelectedCols] = useState(null); // null = use defaults from server

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/attendance/log`, {
        params: { month },
        headers: authHeaders,
      });
      setReport(data);
      if (selectedCols === null && Array.isArray(data.columns_default)) {
        setSelectedCols(new Set(data.columns_default));
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to load attendance log';
      toast.error(msg);
      setReport(null);
    } finally {
      setLoading(false);
    }
    // Toggle handled below.
  };

  useEffect(() => { load(); }, [month]);

  const columnsCatalog = report?.columns_catalog || [];
  const activeColKeys = useMemo(() => {
    if (!selectedCols) return report?.columns_default || [];
    return columnsCatalog.map((c) => c.key).filter((k) => selectedCols.has(k));
  }, [selectedCols, columnsCatalog, report]);

  const toggleCol = (key) => {
    setSelectedCols((prev) => {
      const next = new Set(prev || (report?.columns_default || []));
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const download = async (kind) => {
    if (activeColKeys.length === 0) {
      toast.error('Select at least one column to export.');
      return;
    }
    setExporting(kind);
    try {
      const params = new URLSearchParams({ month, columns: activeColKeys.join(',') });
      const res = await axios.get(`${API}/admin/attendance/log.${kind}?${params.toString()}`, {
        headers: authHeaders,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: kind === 'csv' ? 'text/csv;charset=utf-8' : 'application/pdf',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attendance-${month}.${kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success(`${kind.toUpperCase()} downloaded.`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || `Export ${kind} failed`;
      toast.error(msg);
    } finally {
      setExporting('');
    }
  };

  const toggleExpanded = (adminId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(adminId)) next.delete(adminId); else next.add(adminId);
      return next;
    });
  };

  if (!isSuperAdmin) {
    return (
      <div className="p-8" data-testid="attendance-forbidden">
        <div className="max-w-md rounded-xl border border-red-200 bg-red-50 p-6">
          <div className="font-bold text-red-900">Super admin only</div>
          <p className="text-sm text-red-800 mt-1">Attendance log is restricted to the super admin account.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 py-6 space-y-4 max-w-7xl" data-testid="attendance-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 leading-tight">Attendance log</h1>
          <p className="text-sm text-slate-500 mt-1">
            Monthly attendance across every admin — first login, worked hours, overtime against an 8-hour day.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Calendar className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="pl-9 pr-3 h-10 rounded-lg border border-slate-300 bg-white text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-500"
              data-testid="attendance-month-picker"
            />
          </div>
          <button
            type="button"
            onClick={() => download('csv')}
            disabled={loading || !!exporting}
            className="inline-flex items-center gap-2 h-10 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold disabled:opacity-50"
            data-testid="attendance-export-csv"
          >
            {exporting === 'csv' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
            Export CSV
          </button>
          <button
            type="button"
            onClick={() => download('pdf')}
            disabled={loading || !!exporting}
            className="inline-flex items-center gap-2 h-10 px-3 rounded-lg bg-slate-900 hover:bg-slate-950 text-white text-sm font-bold disabled:opacity-50"
            data-testid="attendance-export-pdf"
          >
            {exporting === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            Export PDF
          </button>
        </div>
      </div>

      {/* Column selector */}
      <div className={HEADCARD}>
        <div className="flex items-center gap-2 mb-2">
          <Filter className="w-4 h-4 text-slate-500" />
          <div className="text-sm font-bold text-slate-800">Export columns</div>
          <div className="text-xs text-slate-500 ml-1">— applies to both CSV and PDF</div>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="attendance-columns-selector">
          {columnsCatalog.map((c) => {
            const active = (selectedCols || new Set(report?.columns_default || [])).has(c.key);
            return (
              <label
                key={c.key}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border cursor-pointer transition ${
                  active
                    ? 'bg-sky-600 text-white border-sky-600'
                    : 'bg-white text-slate-700 border-slate-300 hover:border-sky-400'
                }`}
                data-testid={`attendance-col-${c.key}`}
              >
                <input
                  type="checkbox"
                  className="hidden"
                  checked={active}
                  onChange={() => toggleCol(c.key)}
                />
                {c.label}
              </label>
            );
          })}
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-500" data-testid="attendance-loading">
          <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Loading attendance…
        </div>
      ) : !report || !report.admins || report.admins.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-500" data-testid="attendance-empty">
          No admin users on record yet.
        </div>
      ) : (
        <div className="space-y-3" data-testid="attendance-list">
          {report.admins.map((adm) => {
            const isOpen = expanded.has(adm.admin_id);
            const hasDays = (adm.days || []).length > 0;
            return (
              <div
                key={adm.admin_id}
                className="rounded-2xl border border-slate-200 bg-white overflow-hidden"
                data-testid={`attendance-admin-${adm.admin_id}`}
              >
                <button
                  type="button"
                  onClick={() => toggleExpanded(adm.admin_id)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition"
                >
                  {hasDays ? (
                    isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />
                  ) : <span className="w-4" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-900 truncate">{adm.name || adm.email || '—'}</div>
                    <div className="text-xs text-slate-500 truncate">{adm.email}</div>
                  </div>
                  <div className="hidden sm:flex items-center gap-4 text-[11px] text-slate-600 tabular-nums">
                    <div><b className="text-slate-800">{adm.days_present}</b> days</div>
                    <div>Late: <b className="text-red-700">{adm.total_late_display}</b></div>
                    <div>Worked: <b className="text-slate-800">{adm.total_worked_display}</b></div>
                    <div>Overtime: <b className="text-emerald-700">{adm.total_overtime_display}</b></div>
                  </div>
                </button>
                {isOpen && hasDays && (
                  <div className="border-t border-slate-100 overflow-x-auto">
                    <table className="w-full text-[12px]">
                      <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider">
                        <tr>
                          {activeColKeys.includes('date') && <th className="text-left px-3 py-2">Date</th>}
                          {activeColKeys.includes('duty_start') && <th className="text-left px-3 py-2">Duty start</th>}
                          {activeColKeys.includes('duty_end') && <th className="text-left px-3 py-2">Duty end</th>}
                          {activeColKeys.includes('first_login') && <th className="text-left px-3 py-2">First login</th>}
                          {activeColKeys.includes('last_activity') && <th className="text-left px-3 py-2">Last activity</th>}
                          {activeColKeys.includes('late') && <th className="text-right px-3 py-2">Late</th>}
                          {activeColKeys.includes('worked') && <th className="text-right px-3 py-2">Worked</th>}
                          {activeColKeys.includes('overtime') && <th className="text-right px-3 py-2">Overtime</th>}
                          {activeColKeys.includes('on_time') && <th className="text-center px-3 py-2">On time</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {(adm.days || []).map((d) => (
                          <tr key={d.date} className="text-slate-700" data-testid={`attendance-day-${adm.admin_id}-${d.date}`}>
                            {activeColKeys.includes('date') && <td className="px-3 py-2 font-mono">{d.date}</td>}
                            {activeColKeys.includes('duty_start') && <td className="px-3 py-2">{d.duty_start || '—'}</td>}
                            {activeColKeys.includes('duty_end') && <td className="px-3 py-2">{d.duty_end || '—'}</td>}
                            {activeColKeys.includes('first_login') && <td className="px-3 py-2 tabular-nums">{d.first_login_display || '—'}</td>}
                            {activeColKeys.includes('last_activity') && <td className="px-3 py-2 tabular-nums">{d.last_activity_display || '—'}</td>}
                            {activeColKeys.includes('late') && (
                              <td className={`px-3 py-2 text-right tabular-nums ${d.minutes_late > 0 ? 'text-red-700 font-semibold' : 'text-slate-500'}`}>
                                {d.late_display}
                              </td>
                            )}
                            {activeColKeys.includes('worked') && <td className="px-3 py-2 text-right tabular-nums">{d.worked_display}</td>}
                            {activeColKeys.includes('overtime') && (
                              <td className={`px-3 py-2 text-right tabular-nums ${d.overtime_minutes > 0 ? 'text-emerald-700 font-semibold' : 'text-slate-500'}`}>
                                {d.overtime_display}
                              </td>
                            )}
                            {activeColKeys.includes('on_time') && (
                              <td className="px-3 py-2 text-center">
                                {d.on_time ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-emerald-50 text-emerald-700 font-bold">Yes</span>
                                ) : (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-red-50 text-red-700 font-bold">Late</span>
                                )}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {isOpen && !hasDays && (
                  <div className="border-t border-slate-100 px-4 py-4 text-xs text-slate-500 italic">
                    No attendance recorded for this month.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {report?.month_label && !loading && (
        <div className="text-[11px] text-slate-400 text-right">
          Report generated for <b>{report.month_label}</b> · Muscat time (UTC+4) · Overtime baseline: 8 hours / day
        </div>
      )}
    </div>
  );
};

export default function AdminAttendanceGated() {
  // Feb 2026 — Wrap the page in DutyGate so a non-super admin
  // reaching /admin/attendance while off-duty still hits the lock
  // screen (the AdminLayout gate no longer applies — this page
  // renders as a top-level route).
  return (
    <DutyGate>
      <AdminAttendance />
    </DutyGate>
  );
}
